import { useRef, useState } from "react";
import { Camera, ImageIcon } from "lucide-react";

import { StepFooter } from "@/features/onboarding/ui/IdentityStep";
import { useUploadPhoto } from "@/features/workers/model/useWorkerProfile";
import { CertUpload } from "@/features/workers/ui/CertUpload";
import type { WorkerProfile } from "@/features/workers/types";
import {
  ALLOWED_IMAGE_TYPES,
  IMAGE_ACCEPT_ATTR,
  validateUpload,
} from "@/shared/lib/uploadValidation";

interface Props {
  profile: WorkerProfile;
  onNext: () => void;
  onBack: () => void;
}

export function DocumentsStep({ profile, onNext, onBack }: Props) {
  // Two inputs: one with capture="user" routes to the front camera on phones,
  // one without lets the user pick an existing image. Desktop browsers ignore
  // capture and treat both as a normal file picker — same UX, no breakage.
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = useUploadPhoto();
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Some uploaded formats (notably HEIC) won't render in non-Safari browsers.
  // Track that per render and fall back to the camera placeholder so the user
  // doesn't see the broken-image alt text where their photo should be.
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);

  const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateUpload(file, ALLOWED_IMAGE_TYPES);
    if (err) {
      setPhotoError(err);
      return;
    }
    setPhotoError(null);
    setPhotoLoadFailed(false);
    uploadPhoto.mutate(file);
  };

  const hasPhoto = Boolean(profile.photo_url);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!hasPhoto) return;
        onNext();
      }}
    >
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Profile photo & certifications
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          A clear profile photo is required so venues know who to expect on the
          floor. Certifications are optional but help you get hired faster.
        </p>
      </div>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-copy">
          Profile photo{" "}
          <span className="text-xs font-normal text-copy-subtle">(required)</span>
        </h3>

        <div className="flex items-center gap-4">
          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-border bg-surface-sunken">
            {profile.photo_url && !photoLoadFailed ? (
              <img
                key={profile.photo_url}
                src={profile.photo_url}
                alt="Your profile"
                className="h-full w-full object-cover"
                onError={() => setPhotoLoadFailed(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-copy-subtle">
                <Camera className="h-6 w-6" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploadPhoto.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken disabled:opacity-50"
            >
              <Camera className="h-4 w-4" />
              {uploadPhoto.isPending
                ? "Uploading…"
                : hasPhoto
                  ? "Retake selfie"
                  : "Take selfie"}
            </button>
            <button
              type="button"
              onClick={() => libraryInputRef.current?.click()}
              disabled={uploadPhoto.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken disabled:opacity-50"
            >
              <ImageIcon className="h-4 w-4" />
              {hasPhoto ? "Choose from library" : "Choose from library"}
            </button>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept={IMAGE_ACCEPT_ATTR}
            capture="user"
            onChange={onPhotoChange}
            className="hidden"
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept={IMAGE_ACCEPT_ATTR}
            onChange={onPhotoChange}
            className="hidden"
          />
        </div>

        <div className="rounded-md border border-border bg-surface-sunken p-3">
          <p className="text-xs font-semibold text-copy">
            What makes a good profile photo
          </p>
          <ul className="mt-1.5 space-y-1 text-xs text-copy-muted">
            <li>• Solo picture of just you — no group shots</li>
            <li>• No sunglasses or hats</li>
            <li>• Show your entire face and the top of your shoulders</li>
            <li>• Make sure you have good lighting</li>
          </ul>
        </div>

        <p className="text-xs text-copy-muted">
          JPG, PNG, WebP, GIF, BMP, or HEIC. Max 5 MB.
        </p>
        {photoError && (
          <p className="text-xs text-destructive-foreground">{photoError}</p>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-copy">
          Certifications{" "}
          <span className="text-xs font-normal text-copy-subtle">(optional)</span>
        </h3>
        <p className="text-xs text-copy-muted">
          Smart Serve, Pro Serve, food handler — anything that speeds up your hire. Skip if you
          don't have any yet; venues can still see your profile. We'll warn you 30 days before
          any cert expires.
        </p>
        <CertUpload certifications={profile.certifications} />
      </section>

      <StepFooter
        onBack={onBack}
        isSubmitting={false}
        nextLabel="Continue"
        nextDisabled={!hasPhoto}
      />
    </form>
  );
}
